import { useMutation, useQueryClient } from '@tanstack/react-query';
import { env } from '@/config/env';
import { api, post, put, unwrap, type ApiEnvelope } from '@/lib/api/client';

/**
 * Everything the Edit User drawer can do.
 *
 * The original spread these across 1,452 lines of JSX with the axios call, the
 * notification and the error handling inlined at each of ~18 call sites, each
 * with its own `useState` loading flag. Here every operation is a mutation and
 * the drawer only decides what to render.
 */

/** Boolean switches on a user account. Each is PATCHed on its own. */
export const USER_FLAGS = [
  'AllowEmails',
  'AllowPush',
  'AllowSMS',
  'IsBlocked',
  'IsGuestUser',
  'CanViewBetaStores',
  'CanViewAlphaStores',
  'CanMakeOrders',
  'CanViewBetaInfoCards',
  'IsHighRisk',
] as const;

export type UserFlag = (typeof USER_FLAGS)[number];

export interface UserProfileUpdate {
  FirstName: string;
  LastName: string;
  Email: string;
  PhoneNumber: string;
  BirthDate?: string | null;
  Gender: number;
  PreferredLanguage: number;
  ExternalClientId?: string | null;
}

/**
 * Roles assignable from this screen.
 *
 * Verbatim from the original, including that `BookingAttendance` only appears
 * when the client has a booking service configured.
 */
const BASE_ROLES = [
  'User', 'Admin', 'Delivery', 'Dispatch', 'StoreAdmin', 'StoreManager',
  'SuperAdmin', 'Picker', 'Packer', 'UserAdmin', 'TopAnalytics', 'EBT',
  'DeliveryProvider', 'DeliNotifications', 'Deli', 'Marketing',
  'SelfCheckoutManager', 'CustomerSupport', 'FreshFoodManager', 'PhonePicker',
  'Kitchen', 'DeliManager', 'Finance', 'Merchandiser', 'Demo',
  'CustomerSupportAdmin', 'Owner', 'Inventory', 'ManagedTimeSheets',
];

export const ASSIGNABLE_ROLES = [
  ...BASE_ROLES,
  ...(env.bookingApiUrl ? ['BookingAttendance', 'BookingAttendanceFlex', 'BookingAttendanceRecovery'] : []),
].sort((a, b) => a.localeCompare(b));

/** Anything that changes a user invalidates the search results behind it. */
function useInvalidateCustomers() {
  const queryClient = useQueryClient();
  return () =>
    queryClient.invalidateQueries({ queryKey: ['customers', 'search'] });
}

export function useUpdateProfile(userId: string) {
  const invalidate = useInvalidateCustomers();

  return useMutation({
    mutationFn: (values: UserProfileUpdate) =>
      post<unknown>(`cs/update/user/${userId}`, values),
    onSuccess: invalidate,
  });
}

/**
 * One switch at a time — the endpoint takes a partial body.
 *
 * NOTE: in the original the "Is High Risk" switch sent `CanMakeOrders`, so
 * flagging an account as high risk silently revoked its ability to order and
 * `isHighRisk` was never written at all. Sending the field the label names.
 */
export function useUpdateFlag(userId: string) {
  const invalidate = useInvalidateCustomers();

  return useMutation({
    mutationFn: ({ flag, value }: { flag: UserFlag; value: boolean }) =>
      post<unknown>(`cs/update/user/${userId}`, { [flag]: value }),
    onSuccess: invalidate,
  });
}

export function useToggleRole(userId: string) {
  const invalidate = useInvalidateCustomers();

  return useMutation({
    mutationFn: (role: string) =>
      put<unknown>(`users/userroles/${userId}/${role}`, {}),
    onSuccess: invalidate,
  });
}

/**
 * Several endpoints return the generated secret in `message` rather than
 * `data`, so they cannot go through `unwrap()`.
 */
async function readMessage(request: Promise<{ data: ApiEnvelope<unknown> & { message?: string; success?: boolean } }>) {
  const response = await request;
  const body = response.data;
  if (body?.error) throw new Error(body.error);
  return body?.message ?? '';
}

export function useResetPasswordRandom(userId: string) {
  return useMutation({
    mutationFn: () =>
      readMessage(
        api.put(`cs/users/createdefaultpassword/${userId}/true`, {})
      ),
  });
}

export function useSetPassword(userId: string) {
  return useMutation({
    mutationFn: (password: string) =>
      readMessage(
        api.put('cs/users/changepassword', {
          UserId: userId,
          Password: password,
        })
      ),
  });
}

export function useSendLoginCode(userId: string) {
  return useMutation({
    mutationFn: () => readMessage(api.get(`cs/users/logincode/${userId}`)),
  });
}

export function useResetLoginCode(userId: string) {
  return useMutation({
    mutationFn: () => readMessage(api.get(`cs/users/reset/logincode/${userId}`)),
  });
}

export function useRetrieveLoginCode(userId: string) {
  return useMutation({
    mutationFn: () =>
      readMessage(api.get(`cs/users/logincode/${userId}/retrieve`)),
  });
}

export function useManagerCode(userId: string) {
  return useMutation({
    mutationFn: () => unwrap<string>(api.get(`admin/getmanagercode/${userId}`)),
  });
}

export function useGenerateManagerCode(userId: string) {
  return useMutation({
    mutationFn: () =>
      readMessage(api.post(`admin/generatenewmanagercode/${userId}`, {})),
  });
}

/** Keyed by friendCode, not id — that is what the endpoint takes. */
export function useDeleteUser(friendCode: string) {
  const invalidate = useInvalidateCustomers();

  return useMutation({
    mutationFn: () =>
      readMessage(api.post(`admin/deleteuser/${friendCode}`, {})),
    onSuccess: invalidate,
  });
}
